import clsx from 'clsx';
import filter from 'lodash/filter';
import findIndex from 'lodash/findIndex';
import flatten from 'lodash/flatten';
import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import map from 'lodash/map';
import React from 'react';
import useUpdateEffect from 'react-use/lib/useUpdateEffect';

import { getCache } from 'Apollo';
import { closeCurrentMediaMobileMenu } from 'Apollo/Store/Showroom/UserView/resolvers';
import { GET_SHOWROOM_FILTER_VALUES, GET_SHOWROOM_SECTION_FILTERS } from 'Apollo/Store/Showroom/queries';
import {
	GetShowroomSectionFilterValuesQuery,
	MediaInfoFragment,
	MediaType,
	useGetShowroomSectionFiltersUserViewQuery,
	useGetShowroomSectionsQuery
} from 'generated/graphql';

import { ShowroomFilterTree, ShowroomFilterTreeValue } from 'Types/showroom';

import { useTimeout } from 'lib/Hooks';
import { useGetCurrentShowroomData, useIsShowroomMobileVersion } from 'lib/showroom';
import { buildShowroomFiltersTree, searchMedias, updateFilterValues } from 'lib/showroomFilters';

import { CircularProgress } from '@material-ui/core';

import { ShowroomContext } from 'context/showroom/ShowroomContext';

import ShowroomLayer from 'components/Showroom/Layer';

import ShowroomMediaItem, { ComponentProps as ShowroomMediaItemProps } from '../MediaItem/ShowroomMediaItem';
import ShowroomContentSectionFiltersLayout from './Layout/Filters';
import { RenderItemFn as RenderItemFlexFn } from './Layout/Filters/ShowroomContentSectionFiltersLayout';
import ShowroomContentSectionRowLayout from './Layout/Row';
import { RenderItemFn as RenderItemRowFn } from './Layout/Row/ShowroomContentSectionRowLayout';
import ShowroomSectionActions from './Layout/SectionActions';
import { useStyles } from './styles';

export type ComponentProps = {
	selectedSection: number;
};

const MEDIA_BATCH = 50;

const ShowroomContentSection = (props: ComponentProps) => {
	const { selectedSection } = props;

	const classes = useStyles();

	const { track, getParams } = React.useContext(ShowroomContext);
	const showroom = useGetCurrentShowroomData();

	const { data: showroomSections, loading: loadingSections } = useGetShowroomSectionsQuery({ variables: { id: showroom.id } });

	const sections = React.useMemo(() => showroomSections?.getShowroom.sections, [showroomSections]);

	const isMobileVersion = useIsShowroomMobileVersion();

	const [zoomViewOpen, setZoomViewOpen] = React.useState(false);
	const [initialZoomImage, setInitialZoomImage] = React.useState(0);

	const { run, clear } = useTimeout();

	const showroomId = React.useMemo(() => showroom?.id, [showroom]);
	const currentSection = React.useMemo(() => sections?.[selectedSection], [sections, selectedSection]);
	const sectionMedias = React.useMemo(() => flatten(map(currentSection?.rows, 'medias')), [currentSection]);
	const [filteredMedias, setFilteredMedias] = React.useState(sectionMedias);
	const imageMedias = React.useMemo(() => filter(filteredMedias, media => media.mediaType !== MediaType.Video), [filteredMedias]);

	const [refreshingSearch, setRefreshingSearch] = React.useState(false);
	const [currentMediaPage, setCurrentMediaPage] = React.useState<number>(0);
	const [currentFilterTrees, setCurrentFilterTrees] = React.useState<ShowroomFilterTree[]>([]);
	const [selectedFilters, setSelectedFilters] = React.useState<string[]>([]);
	const [showFilters, setShowFilters] = React.useState<boolean>(false);

	const { data, loading, client } = useGetShowroomSectionFiltersUserViewQuery({
		variables: {
			section: selectedSection,
			params: getParams
		},
		skip:
			currentSection?.options.globalSelectedFilters.galleriesFields.length === 0 &&
			currentSection?.options.globalSelectedFilters.samplesFields.length === 0
	});

	const isFilterSection =
		currentSection?.options.globalSelectedFilters.galleriesFields.length > 0 ||
		currentSection?.options.globalSelectedFilters.samplesFields.length > 0;
	const medias = React.useMemo(() => {
		if (!isFilterSection) return sectionMedias;
		return filteredMedias.slice(0, (currentMediaPage + 1) * MEDIA_BATCH);
	}, [currentMediaPage, filteredMedias, isFilterSection, sectionMedias]);

	const mediasByIndex = React.useMemo(() => keyBy(sectionMedias, 'id'), [sectionMedias]);

	// Preload rest of filters for the other sections with filters
	React.useEffect(() => {
		sections?.forEach((section, index) => {
			if (
				index === 0 ||
				section.options.globalSelectedFilters.galleriesFields.length === 0 ||
				section.options.globalSelectedFilters.samplesFields.length === 0
			)
				return;
			client.query({
				query: GET_SHOWROOM_SECTION_FILTERS,
				variables: {
					section: index,
					params: getParams
				}
			});
		});
	}, [client, getParams, sections]);

	useUpdateEffect(() => {
		setRefreshingSearch(false);
		setSelectedFilters([]);
		setFilteredMedias(sectionMedias);
		setCurrentMediaPage(0);
		clear();
	}, [sectionMedias]);

	React.useEffect(() => {
		if (!isMobileVersion) setShowFilters(!loading && !isEmpty(currentFilterTrees));
	}, [isMobileVersion, loading, currentFilterTrees]);

	React.useEffect(() => {
		if (!data?.showroomSectionFilters) return;
		setCurrentFilterTrees(buildShowroomFiltersTree(data.showroomSectionFilters));
	}, [data?.showroomSectionFilters]);

	const onFilterToggle = React.useCallback(
		(filterValue: ShowroomFilterTreeValue) => {
			let selectedSet = new Set(selectedFilters);
			if (!selectedSet.has(filterValue.value.id)) {
				const addFilter = (filterToAdd: ShowroomFilterTreeValue) => {
					selectedSet.add(filterToAdd.value.id);
					if (!isEmpty(filterToAdd.childs)) filterToAdd.childs.forEach(addFilter);
				};
				addFilter(filterValue);
			} else {
				const deleteFilter = (filterToDelete: ShowroomFilterTreeValue) => {
					selectedSet.delete(filterToDelete.value.id);
					if (!isEmpty(filterToDelete.childs)) filterToDelete.childs.forEach(deleteFilter);
				};
				const deleteFilterParent = (filter: ShowroomFilterTreeValue) => {
					const { parent } = filter;
					if (!parent || !selectedSet.has(parent.value.id)) return;
					selectedSet.delete(parent.value.id);
					deleteFilterParent(parent);
				};
				deleteFilter(filterValue);
				deleteFilterParent(filterValue);
			}
			const newSelectedFilters = Array.from(selectedSet);
			setSelectedFilters(newSelectedFilters);
			setRefreshingSearch(true);
			run(() => {
				let newMedias: MediaInfoFragment[] = [];
				let newBuckets = [];
				if (newSelectedFilters.length === 0) {
					newMedias = sectionMedias;
					newBuckets = data.showroomSectionFilters;
				} else {
					const filtersData: GetShowroomSectionFilterValuesQuery = getCache().readQuery({
						query: GET_SHOWROOM_FILTER_VALUES,
						variables: {
							ids: newSelectedFilters
						}
					});
					const mediaIds = searchMedias(filtersData.showroomFilterValues);
					newMedias = mediaIds.map(mediaId => mediasByIndex[mediaId]);
					newBuckets = updateFilterValues(data.showroomSectionFilters, filtersData.showroomFilterValues);
				}
				setCurrentMediaPage(0);
				setFilteredMedias(newMedias);
				setRefreshingSearch(false);
				setCurrentFilterTrees(buildShowroomFiltersTree(newBuckets));
			}, 200);
		},
		[selectedFilters, run, sectionMedias, data?.showroomSectionFilters, mediasByIndex]
	);

	const onLoadMoreMedias = React.useCallback((page: number) => {
		setCurrentMediaPage(page);
	}, []);

	const layerEnabled = currentSection?.options.zoomLayer || currentSection?.options.productLayer;

	const onPlay = React.useCallback(
		videoId => {
			track([
				{
					eventType: 'click',
					mediaType: 'video',
					mediaId: videoId,
					showroomId
				}
			]);
		},
		[track, showroomId]
	);

	const handleZoomViewClose = React.useCallback(() => {
		setZoomViewOpen(false);
	}, []);

	const handleZoomViewOpen = React.useCallback(
		(id: string) => {
			const index = findIndex(imageMedias, { cloudinaryId: id });
			setInitialZoomImage(index);
			setZoomViewOpen(true);
		},
		[imageMedias]
	);

	const zoom = React.useMemo(() => {
		if (isEmpty(imageMedias) || !layerEnabled) return null;
		return (
			<ShowroomLayer
				sectionOptions={currentSection?.options}
				imageFiles={imageMedias as any}
				initialIndex={initialZoomImage}
				onImageChange={imageId => {
					if (isMobileVersion) closeCurrentMediaMobileMenu();
					track([
						{
							eventType: 'click',
							mediaType: 'image',
							mediaId: imageId,
							showroomId: showroom.id
						}
					]);
				}}
				open={zoomViewOpen}
				onClose={handleZoomViewClose}
				showLabel={currentSection?.options.displayLabel}
			/>
		);
	}, [
		layerEnabled,
		currentSection?.options,
		initialZoomImage,
		zoomViewOpen,
		handleZoomViewClose,
		track,
		showroom.id,
		imageMedias,
		isMobileVersion
	]);

	const onMediaAction = React.useCallback(
		(cloudinaryId: string, type: 'image' | 'video') => {
			if (type === 'image') {
				track([
					{
						eventType: 'click',
						mediaType: 'image',
						mediaId: cloudinaryId,
						showroomId: showroom.id
					}
				]);
				if (layerEnabled) handleZoomViewOpen(cloudinaryId);
			} else onPlay(cloudinaryId);
		},
		[handleZoomViewOpen, onPlay, showroom.id, track, layerEnabled]
	);

	const handleFiltersDisplay = React.useCallback(() => {
		setShowFilters(prev => !prev);
	}, []);

	const renderMediaItem = React.useCallback(
		(media: MediaInfoFragment, additionalProps?: Partial<ShowroomMediaItemProps>) => (
			<ShowroomMediaItem
				media={media}
				displayLabel={currentSection?.options.displayLabel}
				onMediaAction={onMediaAction}
				layerEnabled={layerEnabled}
				{...additionalProps}
			/>
		),
		[currentSection?.options.displayLabel, layerEnabled, onMediaAction]
	);

	const renderMediaRowItem: RenderItemRowFn = React.useCallback(
		(media, { sizes }) =>
			renderMediaItem(media, {
				imageProps: {
					sizes
				}
			}),
		[renderMediaItem]
	);

	const renderMediaFlex: RenderItemFlexFn = React.useCallback(
		(media, sizes) => renderMediaItem(media, { imageProps: { sizes } }),
		[renderMediaItem]
	);

	return loadingSections ? (
		<div id="showroomContentSectionLoading" className={classes.showroomContentSectionLoading}>
			<CircularProgress />
		</div>
	) : (
		<>
			<ShowroomSectionActions
				selectedSection={selectedSection}
				showFilters={showFilters}
				onShowFilters={handleFiltersDisplay}
				loading={loading}
				filteredMedias={filteredMedias as any}
				hasFilters={
					currentSection?.options.globalSelectedFilters.galleriesFields.length > 0 ||
					currentSection?.options.globalSelectedFilters.samplesFields.length > 0
				}
			/>
			{currentSection?.options.globalSelectedFilters.galleriesFields.length > 0 ||
			currentSection?.options.globalSelectedFilters.samplesFields.length > 0 ? (
				<ShowroomContentSectionFiltersLayout
					filters={currentFilterTrees}
					renderItem={renderMediaFlex}
					items={medias}
					totalItems={filteredMedias.length}
					onLoadMoreMedias={onLoadMoreMedias}
					onFilterToggle={onFilterToggle}
					selectedFilters={selectedFilters}
					refreshing={refreshingSearch}
					onCloseFilters={() => setShowFilters(false)}
					showFilters={showFilters}
					loading={loading}
					displayLabel={currentSection?.options?.displayLabel}
				/>
			) : (
				<div className={clsx(classes.showroomGrid, { [classes.edgeToEdge]: currentSection?.options.fullWidth })}>
					<ShowroomContentSectionRowLayout currentSection={currentSection} renderItem={renderMediaRowItem} />
				</div>
			)}
			{zoom}
		</>
	);
};

export default React.memo(ShowroomContentSection);
